/**
 * Options desk layouts.
 *
 * `OPTIONS_DESK_ROWS` / `optionsDeskTree` are the pre-tab single board —
 * every options pane, in workflow order. `/options` no longer opens it (see
 * `OPTIONS_TAB_DESKS` below); it stays because it is still the "Options"
 * workspace in the shared pool (reachable from Layouts), and the two one-shot
 * migrators that rewrote that workspace build it.
 */
import type { WsNode } from "@/lib/workspace/workspacePages";

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export const OPTIONS_DESK_ROWS: ReadonlyArray<ReadonlyArray<string>> = [
  ["opt:quote"],
  ["opt:trade", "opt:chain"],
  ["opt:recommend", "opt:optimize"],
  ["opt:riskProfile", "opt:greekProfile"],
  ["opt:payoff", "opt:probability"],
  ["opt:volatility", "opt:gex"],
  ["opt:positioning", "opt:flow"],
  ["opt:timeSales", "opt:statistics"],
  ["opt:spreadHacker", "opt:earnings"],
  ["opt:strategy", "opt:strikes"],
  ["opt:tradeStats", "opt:positions"],
  ["opt:history", "opt:bets"],
];

export const OPTIONS_DEFAULT_IDS: ReadonlyArray<string> = OPTIONS_DESK_ROWS.flat();

function leaf(component: string): WsNode {
  return { id: nid("l"), t: "leaf", component };
}

function row(ids: ReadonlyArray<string>): WsNode {
  if (ids.length === 1) return leaf(ids[0]!);
  return { id: nid("s"), t: "split", dir: "h", children: ids.map(leaf) };
}

export function optionsDeskTree(): WsNode {
  const rows = OPTIONS_DESK_ROWS.map(row);
  if (rows.length === 1) return rows[0]!;
  return { id: nid("s"), t: "split", dir: "v", children: rows };
}

/**
 * The Options page as four tabs, each its own workspace in the shared pool.
 * One board of 23 panes made you scroll past the tape to reach the trade and
 * gave the chain, the cone and GEX four different expiries. A tab holds one
 * job: work the position (Trade), read the underlying's vol and positioning
 * (Analyze), watch the tape (Flow), find a structure (Scan).
 *
 * `rows` is the first-visit arrangement on a normal window; `wideColumns`
 * seeds a second "(wide)" workspace that a window at least
 * `OPTIONS_WIDE_MIN_PX` wide opens first, so the ultrawide sees a whole tab
 * without scrolling. Both stay one pick apart in Layouts.
 *
 * Underlying, Strategy, Trade stats, Positions and Payoff are not on any tab:
 * Trade already carries the strategy, the headline numbers, the greeks, the
 * payoff graph / table and the legs, and the tab bar carries spot / IV / rank.
 * They stay in the + palette.
 */
export type OptionsTabId = "trade" | "analyze" | "flow" | "scan";

export type OptionsTabDesk = {
  id: OptionsTabId;
  /** Shared-pool workspace name. */
  name: string;
  storageKey: string;
  rows: string[][];
  wide: {
    name: string;
    columns: string[][];
    columnSizes: { widths: number[]; heights: Array<number[] | undefined> };
  };
};

/** Same threshold as Wealth: the Samsung 49 is 3840 CSS px, a laptop ~1500. */
export const OPTIONS_WIDE_MIN_PX = 2800;

export const OPTIONS_TAB_DESKS: ReadonlyArray<OptionsTabDesk> = [
  {
    id: "trade",
    name: "Options · Trade",
    storageKey: "mpp:options:trade:pw:v1",
    rows: [
      ["opt:trade", "opt:chain"],
      ["opt:strikes", "opt:riskProfile"],
      ["opt:greekProfile", "opt:probability", "opt:history"],
    ],
    wide: {
      name: "Options · Trade (wide)",
      columns: [
        ["opt:trade"],
        ["opt:strikes", "opt:chain"],
        ["opt:riskProfile", "opt:greekProfile"],
        ["opt:probability", "opt:history"],
      ],
      columnSizes: { widths: [27, 27, 23, 23], heights: [undefined, [30, 70], [50, 50], [55, 45]] },
    },
  },
  {
    id: "analyze",
    name: "Options · Analyze",
    storageKey: "mpp:options:analyze:pw:v1",
    rows: [
      ["opt:volatility", "opt:gex"],
      ["opt:positioning", "opt:earnings", "opt:bets"],
    ],
    wide: {
      name: "Options · Analyze (wide)",
      columns: [["opt:volatility"], ["opt:gex", "opt:positioning"], ["opt:earnings", "opt:bets"]],
      columnSizes: { widths: [36, 34, 30], heights: [undefined, [50, 50], [65, 35]] },
    },
  },
  {
    id: "flow",
    name: "Options · Flow",
    storageKey: "mpp:options:flow:pw:v1",
    rows: [["opt:flow"], ["opt:timeSales", "opt:statistics"]],
    wide: {
      name: "Options · Flow (wide)",
      columns: [["opt:flow"], ["opt:timeSales"], ["opt:statistics"]],
      columnSizes: { widths: [46, 32, 22], heights: [] },
    },
  },
  {
    id: "scan",
    name: "Options · Scan",
    storageKey: "mpp:options:scan:pw:v1",
    rows: [["opt:optimize", "opt:spreadHacker"], ["opt:recommend"]],
    wide: {
      name: "Options · Scan (wide)",
      columns: [["opt:optimize"], ["opt:spreadHacker"], ["opt:recommend"]],
      columnSizes: { widths: [40, 30, 30], heights: [] },
    },
  },
];

/** Panes deliberately left off every tab (still in the + palette). */
export const OPTIONS_PALETTE_ONLY: ReadonlyArray<string> = [
  "opt:quote",
  "opt:strategy",
  "opt:tradeStats",
  "opt:positions",
  "opt:payoff",
];

export function optionsTabDesk(id: OptionsTabId): OptionsTabDesk {
  return OPTIONS_TAB_DESKS.find((d) => d.id === id) ?? OPTIONS_TAB_DESKS[0]!;
}

/** `?tab=` → tab id. The old Daily Flow deep link `?module=Flow` opens Flow. */
export function optionsTabFromSearch(params: URLSearchParams): OptionsTabId {
  const t = (params.get("tab") ?? "").toLowerCase();
  if (t === "analyze" || t === "flow" || t === "scan" || t === "trade") return t;
  if ((params.get("module") ?? "").toLowerCase() === "flow") return "flow";
  return "trade";
}

/** The tab bar's href for a tab (Trade is the bare route). */
export function optionsTabHref(id: OptionsTabId): string {
  return id === "trade" ? "/options" : `/options?tab=${id}`;
}
